import {
  HTMLAttributes,
  useEffect,
  useState,
  createContext,
  useContext,
  useRef
} from 'react'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'

import { useParams, Link } from '@redwoodjs/router'
import { useVirtualizer } from '@tanstack/react-virtual'

import { ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, PlusIcon, ChevronUpIcon, XMarkIcon, PhotoIcon, } from '@heroicons/react/20/solid'


import { useRequiredContext } from 'src/lib/appContext'

import type {
  PartHierarchy,
  PartTreeNodeFragment,
} from 'types/graphql'

import { isVersionIncrement, validVersion } from 'src/lib/version'

import { SwatchIcon } from '@heroicons/react/24/outline';
import padEnd from 'lodash.padend'
import { Container as TooltipContainer, Message as TooltipMessage, Tooltip2 } from "src/components/Tooltip"
import { letF } from 'api/src/shared/functional'
import { findPartChildren } from 'api/src/lib/bom'

import type {
  HierarchyController,
  ControllerTreeNode,
  SubjectPart,
  EditableAggregator,
} from './hierarchyController'

import {
  HierarchyControllerContext
} from './hierarchyController'
import { PartColumnSelect, PartFilterSelect, allFilters } from '../PartColumnSelector/PartColumnSelector'
import keyBy from 'lodash.keyby'
import { useLifeCycleStages } from 'src/lib/lifecycle'
import Thumbnail from '../ThumbnailViewer'
import HoverPopover from '../HoverPopover'
import { useRightPanel } from '../RightPanel/RightPanel'
import React from 'react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { extractClosestEdge, Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import invariant from 'tiny-invariant'
import EmptyState from '../EmptyState/EmptyState'

export {
  HierarchyControllerContext,
  useHierarchyControllerFactory,
} from './hierarchyController'

export type {
  RootInput,
  HierarchyController,
  HierarchyControllerInput,
  ControllerTreeNode,
  SubjectPart,
  PartHierarchyHeaderColumn,
  ActivatableAggregator,
  EditableAggregator,
  HierarchyMode
} from './hierarchyController'

type PartHierarchyExplorerProps = {
  hierarchyController: HierarchyController
}


const BranchDisabledContext = createContext<boolean>(false)

const HEADER_HEIGHT = 50
const FILTER_HEIGHT = 40

const ROW_HEIGHT = 32

const leftSticky = {
  position: 'sticky',
  left: 0,
  zIndex: 22,
  overflow: 'visible',
  height: ROW_HEIGHT - 6,
  alignItems: 'center',
} as const
interface AggWidthInput {
  name: string
  index: number
}

const PartHierarchy: React.FC<PartHierarchyExplorerProps> = ({
  hierarchyController,
}) => {

  const { roots, selectable, fullScreen, selectedAgg, filterController, allColumns } = hierarchyController

  const rootComponents = roots
    .map((root, i) => {
      return <HierarchyControllerContext.Provider value={{ hierarchyController, root }} key={root.rootNode.address}>
        <PartHierarchyNode node={root.rootNode} depth={0} />
      </HierarchyControllerContext.Provider>
    })

  const parentRef = React.useRef(null)

  const allExpandedNodes: Record<string, boolean> = {}
  const rowCountByRoot = roots.reduce((rowCountByRoot, root, rootIndex) => {
    // we should index the root in the controller, and then use the root index
    // elsewhere to refer to it, or more ideally adjust all the nodes to include
    // the root index as the first part of their hierarchy
    // const { rootIndex } = root

    const treeNodeCount = root.tree.reduce((treeNodeCount, node) => {
      if (node.filterRemove) return treeNodeCount

      // always count the root
      if (node.isTreeRoot) return treeNodeCount + 1

      let anyCollapsedAncestor = false
      const levels = node.partNumberAddress.split('.')
      for (let i = 1; i < levels.length; i++) {
        const ancestorLevel = levels.slice(0, i).join('.')
        anyCollapsedAncestor = anyCollapsedAncestor || !root.expandedNodes[ancestorLevel]
        if (root.expandedNodes[ancestorLevel]) {
          allExpandedNodes[ancestorLevel] = true
        }
      }
      if (!anyCollapsedAncestor) {
        return treeNodeCount + 1
      }

      return treeNodeCount
    }, 0)


    return {
      ...rowCountByRoot,
      [rootIndex]: treeNodeCount
    }
  }, {} as Record<number, number>)

  const selectUiCount = selectable ? Object.values(allExpandedNodes).length + 1 : 0

  // The virtualizer
  const rowVirtualizer = useVirtualizer({
    count: roots.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (rootIndex) => {
      // Row height plus 1px border
      return ((ROW_HEIGHT + 1) * rowCountByRoot[rootIndex]!)
    },
  })

  React.useEffect(() => {
    rowVirtualizer.measure()
  }, [roots])

  const allSelected = roots.every(root => {
    const selectedByLevel = keyBy(root.selectedNodes)
    const allSelected = root.tree.every(node => {
      if (node.filterRemove) {
        return true
      }
      if (!selectedByLevel[node.hierarchy]) {
        return false
      }
      return true
    })

    return allSelected
  })
  const selectAll = <div className='flex items-center cursor-pointer'>
    <input
      onClick={e => e.stopPropagation()}
      type="checkbox"
      onChange={e => {
        if (e.target.checked) {
          hierarchyController.selectAll()
        } else {
          hierarchyController.unselectAll()
        }
      }}
      checked={allSelected}
      aria-describedby="select-all-children"
      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600 cursor-pointer"
    />
  </div>

  const totalRowCount = Object.values(rowCountByRoot).reduce((total, rowCount) => total + rowCount, 0)

  const uiFilters = hierarchyController.hideFilterUi ? [] : filterController.filters
  const filtersHeight = FILTER_HEIGHT * uiFilters.length
  const totalHeight = (((ROW_HEIGHT + 1) * totalRowCount) + HEADER_HEIGHT) + filtersHeight + 18 + (selectUiCount * ROW_HEIGHT)
  const containerHeight = fullScreen ? undefined : totalHeight
  const stages = useLifeCycleStages()

  const primaryColumn = hierarchyController.filteredColumns.find(c => c.primaryColumn)
  invariant(primaryColumn)
  return <>
    {rootComponents.length > 0 ? <div className='overflow-auto h-full w-full' ref={parentRef} style={{ height: containerHeight }}>
    <div
        className='w-fit min-w-full'
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {
          uiFilters.map((f, i) => {
            const selectedFilter = allFilters.find(filter => filter.key === f.type)!
            const selectedColumn = allColumns.find(c => c.fullKey === f.key)!
            return <div
              key={i}
              className='sticky bg-gray-100 text-xs border-t border-gray-200' style={{
                height: FILTER_HEIGHT,
                top: (i * (FILTER_HEIGHT)),
                // hack of the century to make dropdowns not on top of each other
                zIndex: 26 + (uiFilters.length - i)
              }}>
              <div className='fixed left-0 right-0 flex items-center gap-2 pl-10 pr-6' style={{
                position: fullScreen ? 'fixed' : 'sticky',
                height: FILTER_HEIGHT
              }}>
                <div>Filter</div>
                <PartColumnSelect columns={allColumns} value={f.key} onChange={(fullKey) => {
                  const column = allColumns.find(c => c.fullKey === fullKey)!
                  // default to another filter if the type isn't supported
                  const getFilterOrDefault = () => {
                    if (selectedFilter.forType.includes(column.valueType)) {
                      return {
                        type: f.type
                      }
                    }

                    // option not allowed unless there is at least
                    // one stage
                    const defaultStage = stages[0]!.key

                    const value = column.valueType === 'Boolean' ? true
                      : column.valueType === 'Number' ? 0
                        : column.valueType === 'String' ? ''
                          : column.valueType === 'URL' ? ''
                            : column.valueType === 'LifeCycle' ? defaultStage : null

                    const defaultFilter = allFilters.find(filter => filter.forType.includes(column.valueType))!
                    return {
                      type: defaultFilter.key,
                      value
                    }
                  }

                  filterController.updateFilter(i, {
                    ...f,
                    ...getFilterOrDefault(),
                    key: column.fullKey
                  })
                }} />
                <PartFilterSelect value={f.type} forType={selectedColumn.valueType} onChange={(filterType) => {
                  filterController.updateFilter(i, {
                    ...f,
                    type: filterType
                  })
                }} />
                <selectedFilter.Input value={f.value} valueType={selectedColumn.valueType} typeContext={selectedColumn.key} onChange={(v) => {
                  filterController.updateFilter(i, {
                    ...f,
                    value: v
                  })
                }} />
                {/* <div className='flex-1'>{JSON.stringify(f.value)}</div> */}
                <button
                  className='ml-auto'
                  onClick={() => filterController.removeFilter(i)}><XMarkIcon className='w-4 h-4' /></button>
              </div>
            </div>
          })
        }
        <div className='border-t bg-white z-[25] sticky top-0 w-fit min-w-full' style={{
          top: (uiFilters.length * FILTER_HEIGHT)
        }}>
          <PartHierarchyHeader
            hierarchyController={hierarchyController}
            selectedAggIndex={selectedAgg?.index}
            onCreateLocalAggregator={hierarchyController.createLocalAggregator}
          />
        </div>
        <div className='auto w-fit min-w-full'>
          {selectable &&
            <div className='w-full border-t flex text-xs'>
              <div className={`px-5 flex border-gray-200 items-center gap-3 bg-white`} style={{
                ...leftSticky,
                minWidth: `${primaryColumn.state.width}px`,
                height: ROW_HEIGHT,
              }} >
                <div className='w-4 -ml-[14px]' />
                {selectAll}
                Select All
              </div>
            </div>
          }

            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Only the visible items in the virtualizer, manually positioned to be in view */}
              {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  className='border-b border-gray-200'
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {rootComponents[virtualItem.index]}
                </div>
              ))}
            </div>
        </div>
      </div>
    </div>
          : <EmptyState message='There are no parts'/>}
  </>
}

// Define the drag data type for column headers
const COLUMN_DRAG_TYPE = 'column-drag-data' as const;

interface ColumnDragData {
  type: typeof COLUMN_DRAG_TYPE;
  fullKey: string;
  index: number;
}

function isColumnDragData(data: unknown): data is ColumnDragData & Record<string, unknown> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === COLUMN_DRAG_TYPE &&
    'fullKey' in data &&
    typeof (data as ColumnDragData).fullKey === 'string' &&
    'index' in data &&
    typeof (data as ColumnDragData).index === 'number'
  );
}

type ColumnHeaderProps = {
  fullKey: string
  name: React.ReactNode
  width: number
  height: number
  onClick?: () => void
  className?: string
  textAlign?: 'left' | 'center' | 'right'
  isSortColumn?: boolean
  sortDirection?: 'asc' | 'desc'
  onWidthChange: (key: string, width: number) => void
  index?: number
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  fullKey,
  name,
  width,
  height,
  onClick,
  className = '',
  textAlign = 'left',
  isSortColumn = false,
  sortDirection,
  onWidthChange,
  index,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (initialWidth: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX
      const newWidth = Math.max(50, initialWidth + diff)
      onWidthChange(fullKey, newWidth)
      e.preventDefault()
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const ResizeHandle = () => {
    return <div
      className={`absolute cursor-col-resize w-4 items-center flex flex-col h-6 -right-5`}
      onMouseDown={handleMouseDown(width)}
    >
      <div className='border-r border-r-gray-200 flex-1'></div>
    </div>
  }

  const ElementType = onClick ? 'button' : 'div'

  // Set up drag and drop functionality
  useEffect(() => {
    const el = headerRef.current;
    if (el && index !== undefined) {
      const data: Record<string | symbol, unknown> = {
        type: COLUMN_DRAG_TYPE,
        fullKey,
        index
      };

      return combine(
        draggable({
          element: el,
          getInitialData: () => data,
          onDragStart: () => {
            setIsDragging(true);
          },
          onDrop: () => {
            setIsDragging(false);
          }
        }),
        dropTargetForElements({
          element: el,
          getIsSticky: () => true,
          canDrop: ({ source }) => {
            if (!isColumnDragData(source.data)) return false;
            return source.data.index !== index;
          },
          getData: ({ input }) => {
            return attachClosestEdge(data, {
              element: el,
              input,
              allowedEdges: ['left', 'right'],
            });
          },
          onDrag: ({ self, source }) => {
            if (!isColumnDragData(source.data)) {
              return;
            }

            if (source.element === el) {
              setClosestEdge(null);
              return;
            }

            const edge = extractClosestEdge(self.data);
            const sourceIndex = source.data.index;

            const isColumnBeforeSource = index === sourceIndex - 1;
            const isColumnAfterSource = index === sourceIndex + 1;
            const isDropIndicatorHidden =
              (isColumnBeforeSource && edge === 'right') ||
              (isColumnAfterSource && edge === 'left');

            setClosestEdge(isDropIndicatorHidden ? null : edge);
          },
          onDragLeave: () => setClosestEdge(null),
          onDrop: () => setClosestEdge(null),
        })
      );
    }
  }, [headerRef, fullKey, index]);

  return (
    <div
      ref={headerRef}
      className={`relative flex items-center cursor-pointer ${className} ${isDragging ? 'opacity-50' : ''}`}
      style={{
        width,
        height,
      }}
      draggable={false}
      data-column-index={index}
    >
      <ElementType
        onClick={onClick}
        className='flex items-center w-full'
        type={onClick ? 'button' : undefined}
      >
        <div className='shrink-0 overflow-hidden text-ellipsis whitespace-nowrap h-[17px] items-center'
          style={{
            width: isSortColumn ? width - 20 : width,
            textAlign,
          }}
        >
          {name}
        </div>
        {isSortColumn && sortDirection && (
          <div>
            {sortDirection === 'asc' ? <ChevronUpIcon className='w-[20px]' /> : <ChevronDownIcon className='w-[20px]' />}
          </div>
        )}
      </ElementType>
      <ResizeHandle />
      {closestEdge && <DropIndicator edge={closestEdge} gap="24px" />}
    </div>
  )
}

type PartNumberNameColumnHeaderProps = {
  nameColumnWidth: number
  onWidthChange: (width: number) => void
  sortColumn: { fullKey: string }
  sortBy: { direction: 'asc' | 'desc' }
  onSortPartNumber: () => void
  onSortName: () => void
}

const PartNumberNameColumnHeader: React.FC<PartNumberNameColumnHeaderProps> = ({
  nameColumnWidth,
  onWidthChange,
  sortColumn,
  sortBy,
  onSortPartNumber,
  onSortName
}) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = nameColumnWidth

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX
      const newWidth = Math.max(250, startWidth + diff)
      onWidthChange(newWidth)
      e.preventDefault()
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div className='flex items-center mr-2' style={{
      position: 'sticky',
      left: 0,
      zIndex: 22,
      overflow: 'visible',
      height: HEADER_HEIGHT,
      minWidth: `${nameColumnWidth}px`,
    }}>
      <div className={`pl-10 flex items-center bg-white relative`} style={{ width: '100%', height: HEADER_HEIGHT }}>
        <div className="flex items-center w-full">
          <button
            title="Sort by Part Number"
            className="flex items-center mr-2"
            onClick={onSortPartNumber}
          >
            <div className="shrink-0 overflow-hidden text-ellipsis whitespace-nowrap h-[17px] items-center">
              Part Number
            </div>
            {sortColumn.fullKey === 'field.partNumber' && (
              <div>
                {sortBy.direction === 'asc' ? <ChevronUpIcon className='w-[20px]' /> : <ChevronDownIcon className='w-[20px]' />}
              </div>
            )}
          </button>
          /
          <button
            title="Sort by Name"
            className="flex items-center ml-2"
            onClick={onSortName}
          >
            <div className="shrink-0 overflow-hidden text-ellipsis whitespace-nowrap h-[17px] items-center">
              Name
            </div>
            {sortColumn.fullKey === 'field.name' && (
              <div>
                {sortBy.direction === 'asc' ? <ChevronUpIcon className='w-[20px]' /> : <ChevronDownIcon className='w-[20px]' />}
              </div>
            )}
          </button>
        </div>
        <div
          className={`absolute cursor-col-resize w-4 items-center flex flex-col h-6 -right-2`}
          onMouseDown={handleMouseDown}
        >
          <div className='border-r border-r-gray-200 flex-1'></div>
        </div>
      </div>
    </div>
  )
}

type PartHierarchyHeaderProps = {
  selectedAggIndex?: number
  onCreateLocalAggregator?: () => void
  hierarchyController: HierarchyController
}

export const PartHierarchyHeader: React.FC<PartHierarchyHeaderProps> = ({
  onCreateLocalAggregator,
  hierarchyController,
}) => {
  const { filteredColumns, aggregators, showAggregations, sortColumn, sortBy } = hierarchyController;
  const containerRef = useRef<HTMLDivElement>(null);

  // Create a unified array of columns and aggregators
  const columnsAndAggregators = [
    ...filteredColumns
      .filter(c => c.key !== 'partNumberName')
      .map(column => ({
        type: 'column' as const,
        fullKey: column.fullKey,
        name: column.name,
        displayName: column.displayName,
        className: '',
        width: column.state.width,
        isSortColumn: column.fullKey === sortColumn.fullKey,
        textAlign: column.headerAlign || column.align || 'left' as const,
        onClick: () => {
          if (column.type === 'aggregation') {
            hierarchyController.setSelectedAgg(column.key)
            hierarchyController.setToolbarMode('agg')
            return
          }
          const isSortColumn = column.fullKey === sortColumn.fullKey;
          if (!isSortColumn) {
            hierarchyController.setSortBy({
              fullKey: column.fullKey,
              direction: 'asc'
            })
            return
          }
          if (sortBy.direction === 'asc') {
            hierarchyController.setSortBy({
              ...sortBy,
              direction: 'desc'
            })
            return
          }
          hierarchyController.resetSort()
        }
      })),
  ];

  // Monitor for column reordering
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => {
        return isColumnDragData(source.data);
      },
      onDrop: async ({ location, source }) => {
        const target = location.current.dropTargets[0];
        if (!target) return;

        if (!isColumnDragData(source.data)) return;

        const sourceFullKey = source.data.fullKey;

        const targetData = target.data as Record<string | symbol, unknown>;
        const edge = extractClosestEdge(targetData);

        if (!isColumnDragData(targetData)) return;

        const targetFullKey = targetData.fullKey;

        // Determine position based on edge
        const position = edge === 'left' ? 'before' : 'after';

        // Move the column using the new moveColumn function
        hierarchyController.moveColumn(position, sourceFullKey, targetFullKey);
      }
    });
  }, [filteredColumns, hierarchyController]);

  const primaryColumn = hierarchyController.filteredColumns.find(c => c.primaryColumn)
  invariant(primaryColumn)
  return <div className={`flex text-xs pr-2`}>
    <PartNumberNameColumnHeader
      nameColumnWidth={primaryColumn.state.width}
      onWidthChange={(w) => hierarchyController.setColumnWidth(primaryColumn.fullKey, w)}
      sortColumn={sortColumn}
      sortBy={sortBy}
      onSortPartNumber={() => {
        const isSortPartNumber = sortColumn.fullKey === 'field.partNumber';
        if (!isSortPartNumber) {
          hierarchyController.setSortBy({
            fullKey: 'field.partNumber',
            direction: 'asc'
          })
          return
        }
        if (sortBy.direction === 'asc') {
          hierarchyController.setSortBy({
            ...sortBy,
            direction: 'desc'
          })
          return
        }
        hierarchyController.resetSort()
      }}
      onSortName={() => {
        const isSortName = sortColumn.fullKey === 'field.name';
        if (!isSortName) {
          hierarchyController.setSortBy({
            fullKey: 'field.name',
            direction: 'asc'
          })
          return
        }
        if (sortBy.direction === 'asc') {
          hierarchyController.setSortBy({
            ...sortBy,
            direction: 'desc'
          })
          return
        }
        hierarchyController.resetSort()
      }}
    />
    <div ref={containerRef} className='flex gap-6 items-center overflow-hidden relative'>
      {columnsAndAggregators.map((item, index) => {
        const name = typeof item.name === 'string' ? item.name : item.name(hierarchyController)
        return <ColumnHeader
          key={item.fullKey}
          fullKey={item.fullKey}
          name={item.displayName ? item.displayName(hierarchyController) : item.name}
          width={item.width}
          height={HEADER_HEIGHT}
          onClick={item.onClick}
          className={item.className}
          textAlign={item.textAlign}
          isSortColumn={item.isSortColumn}
          sortDirection={item.isSortColumn ? sortBy.direction : undefined}
          onWidthChange={hierarchyController.setColumnWidth}
          index={index}
        />
      })}
      {(showAggregations) ?
        <TooltipContainer>
          <TooltipMessage position='leftHorizontal' className='right-8'>
            Add New Aggregation
          </TooltipMessage>
          <button className='flex w-5 h-5 justify-center items-center mx-3 hover:bg-gray-200 rounded' type='button' onClick={onCreateLocalAggregator}>
            <PlusIcon className='w-4 h-4' />
          </button>
        </TooltipContainer>
        : null}
    </div>
  </div>
}

type Padding = {
  partNumber: number
}

type PartHierarchyNodeProps = {
  padding?: Padding
  node: ControllerTreeNode
  parentKey?: string
  depth: number
}
const PartHierarchyNode: React.FC<PartHierarchyNodeProps> = ({
  node,
  padding,
  depth,
}) => {
  const {
    root,
    hierarchyController: {
      filteredColumns,
      selectable,
      usageContext,
      showAggregations,
      sortBy,
      sortColumn,
    },
  } = useRequiredContext(HierarchyControllerContext)
  const {
    tree,
    subjectParts,
    expandedNodes,
    setExpanded,
    selectedNodes,
    setSelectedNodes,
  } = root

  const branchDisabled = useContext(BranchDisabledContext)

  const { panelRoute, params } = useRightPanel()

  const isGroup = node.part.nodeType === 'PartGroup'
  const nodeId = node.partNumberAddress
  const expanded = expandedNodes[nodeId]

  const children = findPartChildren(tree, node.hierarchy).sort((childA, childB) => {
    // Check for sortOverride on either root node
    if (childA.sortOverride !== undefined || childB.sortOverride !== undefined) {
      const aSort = childA.sortOverride ?? Number.MAX_SAFE_INTEGER
      const bSort = childB.sortOverride ?? Number.MAX_SAFE_INTEGER
      return aSort - bSort // 0 sorts first, undefined sorts last
    }
    // Handle sort direction before the default sort
    if (sortBy.direction === 'asc') {
      return -1 * sortColumn.sortFn(childA, childB)
    }
    return sortColumn.sortFn(childA, childB)
  }).map((n, i) => {
    return {
      ...n,
      // Ensures parent is always different shade to first child
      shadeNumber: (i + 1 + node.shadeNumber) % 2
    }
  })

  const nodeProps = subjectParts?.find(p => p.hierarchy === node.hierarchy)

  const expandable = Boolean(children.filter(c => !c.filterRemove).length)
  const iconClass = 'h-4 w-4'

  const partActive = params?.partId &&  node.part.id === params?.partId

  const toggleClass = twMerge(classNames(
    'flex items-center bg-white box-content pr-2 border-t',
    {
      'ring-1 ring-inset ring-blue-600': partActive,
      'bg-blue-100': !nodeProps?.highlightColor && partActive,
      'bg-gray-50': !nodeProps?.highlightColor && !partActive && (node.shadeNumber === 0),
      'bg-red-50': nodeProps?.highlightColor === 'red',
      'bg-green-50': nodeProps?.highlightColor === 'green',
      'bg-yellow-50': nodeProps?.highlightColor === 'yellow',
    }
  ))


  const toggleExpand = () => {
    if (expandable) setExpanded(nodeId, !expanded)
  }

  const expandButton = () => {
    const icon = expanded ?
      <ChevronDownIcon data-testid='collapse-hierarchy' className={iconClass} /> :
      <ChevronRightIcon data-testid='expand-hierarchy' className={iconClass} />

    const className = expandable ? ' text-gray-500 hover:bg-gray-900/10 cursor-pointer rounded-md p-0.5' : 'p-0.5'

    return <div className={className} onClick={toggleExpand}>
      {expandable ? icon : <div className={iconClass} />}
    </div>
  }

  //TODO: consider if this is still right
  const checked = selectedNodes.includes(node.hierarchy)
  const disableChildren = branchDisabled || (!checked);

  const selectBox = () => {
    if (!selectable) return null
    return <div className='flex items-center cursor-pointer'>
      <input
        onClick={e => e.stopPropagation()}
        id="hierarchy-node-toggle"
        data-testid={`hierarchy-node-checkbox-${node?.part?.partNumber}`}
        name="hierarchy-node"
        type="checkbox"
        onChange={e => {
          let newNodes: typeof selectedNodes
          if (e.currentTarget.checked) {
            if (!expanded) {
              //when node is collapsed, include children automatically
              const allDecendants = tree
                .filter(h => h.hierarchy.startsWith(node.hierarchy + '.'))
                .map(d => d.hierarchy)
              newNodes = Array.from(new Set([...selectedNodes, node.hierarchy, ...allDecendants]))
            }
            else {
              newNodes = [...selectedNodes, node.hierarchy]
            }
          }
          else {
            const dSet = new Set(tree
              .filter(h => h.hierarchy.startsWith(node.hierarchy + '.'))
              .map(d => d.hierarchy))
            newNodes = selectedNodes.filter(n => n !== node.hierarchy && !dSet.has(n))
          }

          setSelectedNodes(newNodes)
        }}
        checked={checked}
        disabled={branchDisabled}
        aria-describedby="hierarchy-node-checkbox"
        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600 cursor-pointer"
      />
    </div>
  }

  const thickIndentationLevel: HTMLAttributes<HTMLElement>['style'] = {
    paddingLeft: `${24 + (depth * 12)}px`
  }

  const indentationLevel: HTMLAttributes<HTMLElement>['style'] = {
    paddingLeft: `${depth * 12}px`
  }
  const SPACE = '\u00A0'
  if (node.filterRemove) return null

  const columnValues = filteredColumns.map(c => {
    if (c.type === 'field' || c.type === 'aggregation') {
      return {
        ...c,
        value: c.displayFn(node, {root})
      }
    }
    if (c.type === 'metadata') {
      const m = node.part.resolvedMetadata[c.key]
      return {
        ...c,
        value: m?.displayValue || '-'
      }
    }
    throw new Error('Invalid column type')
  }).filter(c => {
    return c.key !== 'partNumberName'
  })

  const partNumberPadding = padding?.partNumber ? padding.partNumber + 1 : undefined
  const partNumber = padEnd(`#${node.part.partNumber}`, partNumberPadding, SPACE)

  const message = nodeProps?.message || null

  // Hack because the version summary component changes the version string
  // to illustrate a version change
  const getVersionLink = () => {
    if (usageContext?.changeOrder) {
      return panelRoute('partChanges', {
            partNumber: node.part.partNumber,
            orderNumber: usageContext!.changeOrder.number,
            partId: node.part.id
          })
    }

    return panelRoute('part', {
          partNumber: node.part.partNumber,
          version: node.part.version,
          partId: node.part.id
        })
  }

  const tools = nodeProps?.tools
  const partNameInner = <>
    <div className='whitespace-nowrap'>{partNumber}</div>
    {isGroup && <Tooltip2
        content='Part Choice'
      ><SwatchIcon className='w-4' /></Tooltip2>}
    {node.part.name}
  </>
  const partNameCell = () => {
    return <Link to={getVersionLink()} className='gap-2 absolute left-0 right-0 bg-inherit h-[28px] flex items-center overflow-hidden text-ellipsis whitespace-nowrap hover:underline underline-offset-4'>
      {partNameInner}
    </Link>
  }

  const primaryColumn = filteredColumns.find(c => c.primaryColumn)
  invariant(primaryColumn)

  return <div key={node.address} className='text-[13px]'>
    <div className={toggleClass} style={{ height: ROW_HEIGHT }}>
      <div className={`gap-1.5 flex bg-inherit pr-1 group relative ml-[1px] mr-2`} style={{
        ...leftSticky,
        width: `${primaryColumn.state.width}px`,
      }}>
        {nodeProps?.leftMark}
        <div style={indentationLevel}/>
        {expandButton()}
        {selectBox()}
        <div className='flex gap-1 group-hover:opacity-100 opacity-0 transition-opacity text-gray-500 absolute right-0 z-10'>
          {tools && tools({ controllerRoot: root })}
        </div>
        <div className='relative flex-1 bg-inherit flex items-center whitespace-nowrap font-medium'>
          {node.mainCell || partNameCell()}
        </div>
      </div>
      <div className='flex flex-row justify-end gap-6'>
        {
          (columnValues)
            .map(f => {
              return <div
                style={{ width: f.state.width }}
                key={`${f!.type}.${f!.key}`}
                className='group shrink-0 text-right whitespace-nowrap overflow-hidden text-ellipsis hover:overflow-visible hover:z-10'>
                <div className='group-hover:bg-inherit px-2 -mx-2 flex items-center' style={{ justifyContent: alignToFlexAlign(f?.align) }}>
                  <div>{f!.value}</div>{f?.key === 'version' && message}
                </div>
              </div>
            })
        }
        <CreateAggSpacer show={showAggregations} />
      </div>
    </div>
    {(expanded && children.filter(n => !n.filterRemove).length) ? letF(
      children.reduce((padding, child) =>
        ({ partNumber: Math.max(child.part.partNumber.length, padding.partNumber) }),
        { partNumber: 0 } as { partNumber: number }),
      childPadding =>
        <div className='flex'>
          <div className='flex flex-col flex-1'>
            <BranchDisabledContext.Provider value={disableChildren}>
              {selectable &&
                <div className='w-full border-t flex'>
                  <div className={`px-3 text-xs flex border-gray-200 items-center gap-3 bg-white`} style={{
                    ...leftSticky,
                    width: `${primaryColumn.state.width}px`,
                    ...thickIndentationLevel,
                    height: ROW_HEIGHT,
                  }} >
                    <div className='w-4 -ml-[7px]' />
                    <SelectAllBox node={node} />
                    Select All Children
                  </div>
                  <div className='bg-green-20 shrink-0 flex gap-6' /*make row the correct width*/ >
                    {(filteredColumns).filter(c => c.key !== 'partNumberName').map(f =>
                      <div style={{ width: f.state.width }} className='shrink-0' key={f.fullKey} />)}
                  </div>
                </div>
              }
              {children.filter(n => !n.filterRemove).map(c =>
                <PartHierarchyNode depth={depth + 1} key={c.address} node={c} padding={childPadding}
                  parentKey={node.hierarchy} />
              )}
            </BranchDisabledContext.Provider>
          </div>
        </div>
    )
      : null
    }
  </div>
}

type SelectAllBoxProps = {
  node: ControllerTreeNode
}
const SelectAllBox: React.FC<SelectAllBoxProps> = ({ node }) => {
  const {
    root: {
      tree,
      selectedNodes,
      setSelectedNodes,
    },
  } = useRequiredContext(HierarchyControllerContext)

  const branchDisabled = useContext(BranchDisabledContext)
  const allDecendants = tree
    .filter(h => h.hierarchy.startsWith(node.hierarchy + '.'))

  const selectedByLevel = keyBy(selectedNodes)
  const allSelected = allDecendants.every(node => {
    if (node.filterRemove) {
      return true
    }
    if (!selectedByLevel[node.hierarchy]) {
      return false
    }
    return true
  })

  const allDescendentLevels = [...allDecendants.map(n => n.hierarchy)]
  const allDescendentsByLevel = keyBy(allDescendentLevels)

  return <div className='flex items-center cursor-pointer'>
    <input
      onClick={e => e.stopPropagation()}
      name="dependencyControl"
      type="checkbox"
      onChange={e => {
        setSelectedNodes(
          e.target.checked ?
            Array.from(new Set([...selectedNodes, ...allDescendentLevels])) :
            selectedNodes.filter(n => !allDescendentsByLevel[n])
        )
      }}
      checked={allSelected}
      disabled={branchDisabled}
      aria-describedby="select-all-children"
      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600 cursor-pointer"
    />
  </div>
}

export const addVersionWarnings = (tree: PartTreeNodeFragment[], existingSubjectParts?: SubjectPart[]) => {
  const outOfDateRefsByLevel = tree.reduce((byLevel, n) => {
    if (!validVersion(n.part.version as string)) return byLevel
    if (!validVersion(n.part.proto.currentVersionString)) return byLevel
    if (!n.part.proto.currentVersionString) return byLevel
    if (!isVersionIncrement(n.part.version, n.part.proto.currentVersionString)) return byLevel

    return {
      ...byLevel,
      [n.hierarchy]: true as const
    }
  }, {} as Record<string, true>)

  const outOfDateParentsByLevel = Object.keys(outOfDateRefsByLevel)
    .reduce((parentsByLevel, level) => {
      const levelParts = level.split(/\./g)
      return {
        ...parentsByLevel,
        [levelParts.slice(0, -1).join('.')]: true as const
      }
    }, {} as Record<string, true>)

  const outOfDateAncestorsByLevel = Object.keys(outOfDateRefsByLevel)
    .reduce((ancestorsByLevel, level) => {
      const levelParts = level.split(/\./g)
      const nextAncestors = levelParts.reduce(({ levelAcc, ancestorsByLevel }, level) => {
        const nextLevel = [...levelAcc, level]
        return {
          ancestorsByLevel: {
            ...ancestorsByLevel,
            [nextLevel.join('.')]: true as const
          },
          levelAcc: nextLevel
        }
      }, { ancestorsByLevel, levelAcc: [] as string[] })
      return nextAncestors.ancestorsByLevel
    }, {} as Record<string, true>)

  const subjectParts = [...tree].map(n => {
    const existingSubject = (existingSubjectParts?.find(s => s.hierarchy === n.hierarchy))

    const partRefOutOfDate = outOfDateRefsByLevel[n.hierarchy]
    if (partRefOutOfDate && !n.isTreeRoot) {
      return {
        ...(existingSubject || {}),
        hierarchy: n.hierarchy,
        message: <div>
          <TooltipContainer className='relative'>
            <ExclamationTriangleIcon className='w-4 ml-1 text-yellow-500' />
            <TooltipMessage className="bottom-10">
              Newer version v{n.part.proto.currentVersionString} available
            </TooltipMessage>
          </TooltipContainer>
        </div>
      }
    }

    const childrenOutOfDate = outOfDateParentsByLevel[n.hierarchy]
    if (childrenOutOfDate) {
      return {
        ...(existingSubject || {}),
        hierarchy: n.hierarchy,
        message: <div>
          <TooltipContainer className='relative'>
            <ExclamationTriangleIcon className='w-4 ml-1 text-yellow-500' />
            <TooltipMessage className="bottom-10">
              Uses out of date parts
            </TooltipMessage>
          </TooltipContainer>
        </div>
      }
    }

    const descendentsOutOfDate = outOfDateAncestorsByLevel[n.hierarchy]
    if (descendentsOutOfDate) {
      return {
        ...(existingSubject || {}),
        hierarchy: n.hierarchy,
        message: <div>
          <TooltipContainer className='relative'>
            <ExclamationTriangleIcon className='w-4 ml-1 text-yellow-500' />
            <TooltipMessage className="bottom-10">
              Parts in this tree are out of date
            </TooltipMessage>
          </TooltipContainer>
        </div>
      }
    }
    return existingSubject
  }).filter(Boolean) as SubjectPart[]
  return subjectParts
}

const CreateAggSpacer = ({ show }: { show?: boolean }) =>
  show ? <div className='inline-block w-5 mx-3' /> : null

const alignToFlexAlign = (input?: 'center' | 'left' | 'right') => {
  if (input === 'center') return 'center'
  if (input === 'right') return 'end'

  // left is default
  return 'start'
}

export interface ArtifactHoverPopoverProps {
  className?: string
  artifact?: {
    id: number
    fileId: number
    file: {
      url: string
      timestamp: string
    }
  }
}

// Example usage for ArtifactHoverPopover
export function ArtifactHoverPopover({ artifact, className }: ArtifactHoverPopoverProps) {
  if (!artifact) return null;

  return (
    <HoverPopover
      trigger={<PhotoIcon className="w-4 h-4 text-gray-600 relative top-0.5" />}
      content={<Thumbnail artifact={artifact} className="w-full h-full" />}
      className={className}
    />
  );
}

/*
const isSyntheticDelete = (node: ControllerTreeNode | SyntheticDeleteNode | GroupNode): node is SyntheticDeleteNode =>
  'type' in node && node.type === 'syntheticDelete'
*/


export default PartHierarchy
